import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import ThumbImage from '@/components/base/ThumbImage';
import { T } from '@/hooks/useAutoTranslate';

interface CaseItem {
  id: number;
  title: string;
  tag: string;
  content: string;
  img_url: string;
  published_at: string;
  featured?: boolean;
}

interface ShareModalProps {
  url: string;
  title: string;
  onClose: () => void;
}

function ShareModal({ url, title, onClose }: ShareModalProps) {
  const { t } = useTranslation();
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const shareLinks = [
    { name: 'WeChat', icon: 'ri-wechat-line', color: 'bg-[#07c160]', action: () => alert('请在微信中使用分享功能') },
    { name: 'Telegram', icon: 'ri-telegram-line', color: 'bg-[#0088cc]', href: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}` },
    { name: 'Facebook', icon: 'ri-facebook-line', color: 'bg-[#1877f2]', href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}` },
    { name: 'Twitter', icon: 'ri-twitter-x-line', color: 'bg-[#1da1f2]', href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}` },
    { name: 'WhatsApp', icon: 'ri-whatsapp-line', color: 'bg-[#25d366]', href: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}` },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl shadow-xl p-6 w-72">
        <h3 className="font-bold text-[#1a3a5c] text-base mb-1">{t('cases_share_modal_title')}</h3>
        <p className="text-gray-400 text-xs mb-5">{t('cases_share_modal_desc')}</p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          {shareLinks.map((s) =>
            s.href ? (
              <a key={s.name} href={s.href} target="_blank" rel="nofollow noopener noreferrer"
                className={`w-11 h-11 flex items-center justify-center rounded-full ${s.color} text-white cursor-pointer hover:opacity-90 transition-opacity`}>
                <i className={`${s.icon} text-lg`}></i>
              </a>
            ) : (
              <button key={s.name} onClick={s.action}
                className={`w-11 h-11 flex items-center justify-center rounded-full ${s.color} text-white cursor-pointer hover:opacity-90 transition-opacity`}>
                <i className={`${s.icon} text-lg`}></i>
              </button>
            )
          )}
        </div>
        <button onClick={onClose} className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-full hover:bg-gray-100">
          <i className="ri-close-line"></i>
        </button>
      </div>
    </div>
  );
}

const CATEGORY_TABS = [
  { key: 'all', labelKey: 'cases_cat_all', icon: undefined },
  { key: '单代孕', labelKey: 'cases_cat_surrogacy', icon: 'ri-heart-pulse-line' },
  { key: '三代试管', labelKey: 'cases_cat_ivf', icon: 'ri-dna-line' },
  { key: '试管+代孕', labelKey: 'cases_cat_full', icon: 'ri-shield-check-line' },
];

export default function CasesSection() {
  const { t } = useTranslation();
  const [cases, setCases] = useState<CaseItem[]>([]);
  const [shareCase, setShareCase] = useState<CaseItem | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCases = async () => {
      setError('');
      try {
        const { data, error: supaErr } = await supabase
          .from('success_cases')
          .select('id, title, tag, content, img_url, published_at, featured, sort_order')
          .eq('status', 'published')
          .order('sort_order', { ascending: true })
          .order('published_at', { ascending: false });
        if (supaErr) throw supaErr;
        setCases(data ?? []);
      } catch (err: any) {
        setError(err?.message || '加载失败');
      }
    };
    fetchCases();
  }, []);

  const filteredCases = (activeTab === 'all' ? cases : cases.filter((c) => c.tag === activeTab)).slice(0, 3);

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="text-[#c9a96e] text-sm font-medium tracking-widest uppercase mb-2">{t('cases_section_label')}</div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a5c]">{t('cases_title')}</h2>
            <p className="text-gray-500 text-sm mt-2">{t('cases_filter_desc')}</p>
          </div>
          <Link to="/cases" className="flex items-center gap-1 text-[#c9a96e] text-sm font-medium hover:gap-2 transition-all cursor-pointer whitespace-nowrap">
            {t('cases_view_all')} <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-8">
          {CATEGORY_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-[#1a3a5c] text-white'
                  : 'bg-[#f8f5f0] text-gray-500 hover:bg-[#f0e8d8] hover:text-[#1a3a5c]'
              }`}
            >
              {tab.icon && <i className={`${tab.icon} text-xs`}></i>}
              {t(tab.labelKey)}
            </button>
          ))}
        </div>

        {error ? (
          <div className="text-center py-12">
            <i className="ri-error-warning-line text-3xl text-red-300 mb-3 block"></i>
            <p className="text-red-500 text-sm mb-3">{error}</p>
            <button onClick={() => window.location.reload()} className="text-[#c9a96e] text-sm cursor-pointer">{t('retry')}</button>
          </div>
        ) : filteredCases.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-[#f8f5f0] rounded-2xl h-72 animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {filteredCases.map((c) => (
              <div key={c.id} className="group bg-[#f8f5f0] rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-300">
                <div className="w-full h-52 overflow-hidden relative">
                  <ThumbImage src={c.img_url} alt={c.title} title={`${c.title} - 好孕案例`} fallbackSrc="https://readdy.ai/api/search-image?query=happy%20Asian%20family%20with%20newborn%20baby%20warm%20champagne%20tones%2C%20joyful%20parents%20holding%20infant%2C%20soft%20natural%20lighting%2C%20editorial%20family%20photography%20with%20clean%20background%2C%20emotional%20happiness&width=600&height=400&seq=case-fb-001&orientation=landscape" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-3 left-3">
                    <span className="inline-block bg-[#1a3a5c]/80 text-[#c9a96e] text-xs font-medium px-3 py-1 rounded-full backdrop-blur-sm"><T text={c.tag} /></span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-[#1a3a5c] text-sm leading-snug mb-2"><T text={c.title} /></h3>
                  <p className="text-gray-500 text-xs leading-relaxed mb-4 line-clamp-2"><T text={c.content} /></p>
                  <div className="flex items-center justify-between">
                    <button onClick={() => setShareCase(c)} className="flex items-center gap-1 text-gray-400 hover:text-[#c9a96e] text-xs transition-colors cursor-pointer ml-auto">
                      <i className="ri-share-line"></i>{t('cases_share')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {shareCase && (
        <ShareModal
          url={`${window.location.origin}/cases/${shareCase.id}`}
          title={shareCase.title}
          onClose={() => setShareCase(null)}
        />
      )}
    </section>
  );
}